/**
 * 사용자 관리 API 서비스
 * - 사용자 CRUD 작업
 * - 강사/학생 생성
 * - 강사-학생 할당
 */

import axios from '../config/axios';
import type {
  User,
  UserCreateRequest,
  UserUpdateRequest,
} from '../types/user';

/**
 * 사용자 목록 조회 파라미터
 */
export interface UserListParams {
  page?: number;
  page_size?: number;
  role?: string;
  group_id?: string;
  search?: string;
  is_active?: boolean;
}

/**
 * 사용자 목록 응답
 */
export interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

/**
 * 강사 생성 요청
 */
export interface InstructorCreateRequest {
  email: string;
  name: string;
  phone?: string;
  password?: string;
}

/**
 * 학생 생성 요청
 */
export interface StudentCreateRequest {
  name: string;
  phone?: string;
  student_id: string;
  password?: string;
  instructor_id?: string;
  grade?: number;  // 학년 (1-12)
  school?: string;  // 학교명
}

/**
 * 강사-학생 대량 할당 요청
 */
export interface InstructorStudentBulkRequest {
  instructor_id: string;
  student_ids: string[];
}

/**
 * 사용자 서비스 클래스
 */
class UserService {
  /**
   * 사용자 목록 조회
   */
  async getUsers(params: UserListParams = {}): Promise<UserListResponse> {
    const response = await axios.get<UserListResponse>('/users', {
      params,
    });
    return response.data;
  }

  /**
   * 사용자 상세 조회
   */
  async getUser(userId: string): Promise<User> {
    const response = await axios.get<User>(`/users/${userId}`);
    return response.data;
  }

  /**
   * 사용자 생성
   */
  async createUser(data: UserCreateRequest): Promise<User> {
    const response = await axios.post<User>('/users', data);
    return response.data;
  }

  /**
   * 강사 생성
   */
  async createInstructor(data: InstructorCreateRequest): Promise<User> {
    const response = await axios.post<User>('/users/instructor', data);
    return response.data;
  }

  /**
   * 학생 생성
   */
  async createStudent(data: StudentCreateRequest): Promise<User> {
    const response = await axios.post<User>('/users/student', data);
    return response.data;
  }

  /**
   * 사용자 수정
   */
  async updateUser(userId: string, data: UserUpdateRequest): Promise<User> {
    const response = await axios.put<User>(`/users/${userId}`, data);
    return response.data;
  }

  /**
   * 사용자 삭제
   */
  async deleteUser(userId: string): Promise<void> {
    await axios.delete(`/users/${userId}`);
  }

  /**
   * 강사-학생 대량 할당
   */
  async assignStudentsToInstructor(data: InstructorStudentBulkRequest): Promise<void> {
    await axios.post('/users/assign-bulk', data);
  }

  /**
   * 강사-학생 대량 할당 해제
   */
  async unassignStudentsFromInstructor(data: InstructorStudentBulkRequest): Promise<void> {
    await axios.post('/users/unassign-bulk', data);
  }

  /**
   * 특정 강사의 학생 목록 조회
   */
  async getInstructorStudents(instructorId: string): Promise<User[]> {
    const response = await axios.get<User[]>(`/users/instructor/${instructorId}/students`);
    return response.data;
  }

  /**
   * 현재 로그인한 강사의 담당 학생 목록 조회
   * @returns 담당 학생 목록
   */
  async getMyStudents(): Promise<User[]> {
    const response = await axios.get<User[]>('/users/my-students');
    return response.data;
  }
}

export const userService = new UserService();
